import { Button, Space, Tag } from 'antd';
import axios from 'axios';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import ColumnSettings from '../../components/table/ColumnSettings';
import PPResizableTable from '../../components/table/PPResizableTable';
import { useColumnSettings } from '../../components/table/useColumnSettings';
import PPTableLink from '../../components/link/PPTableLink';
import PPPageHeader from '../../components/pageHeader/PPPageHeader';
import ProductImage from '../../components/productImage';
import { useTableFilter } from '../../utils/Filter';
import { getIndexColumn } from '../../utils/getIndexColumn';

import './Product.less';
import { PlusOutlined } from '@ant-design/icons';

const routes = [
  {
    path: '',
    breadcrumbName: 'Бараа',
  },
  {
    path: '',
    breadcrumbName: 'Багц бараа',
  },
];

const STATUS_ENUM = {
  ready: 'Идэвхтэй',
  unavailable: 'Идэвхгүй',
};

const EXPIRE_ENUM = {
  true: 'Тийм',
  false: 'Үгүй',
};

const getColumns = (location, getColumnSearchProps, getColumnFilterProps, pagination, meta) => [
  getIndexColumn(pagination.current, pagination.pageSize),
  {
    title: 'Барааны нэр',
    dataIndex: 'title',
    key: 'title',
    ...getColumnSearchProps('title', 'Барааны нэр'),
    render: (text, record) => (
      <PPTableLink
        to={{
          pathname: `/merchant/group/products/${record._id}`,
          state: {
            search: location.search,
          },
        }}
      >
        {text}
      </PPTableLink>
    ),
    width: 200,
  },
  {
    title: 'Ангилал',
    dataIndex: 'category',
    key: 'category',
    filters: (meta?.category?.enum ?? []).map((value) => ({
      text: meta?.category?.enumDictionary?.[value] ?? value,
      value,
    })),
    ...getColumnFilterProps('category'),
    render: (category) => meta?.category?.enumDictionary?.[category] ?? category ?? '-',
    width: 200,
  },
  {
    title: 'Барааны SKU',
    dataIndex: 'productSku',
    key: 'productSku',
    ...getColumnSearchProps('productSku', 'Барааны SKU'),
    width: 200,
  },

  {
    title: 'Баркод',
    dataIndex: 'barCodes',
    key: 'barCodes',
    render: (barCodes) =>
      Array.isArray(barCodes) && barCodes.length
        ? barCodes.map((code) => <Tag key={code}>{code}</Tag>)
        : '-',

    ...getColumnSearchProps('barCodes', 'Баркод'),
    width: 200,
  },

  {
    title: 'Зураг',
    key: 'imageCover',
    width: 200,
    render: (_, record) => (
      <ProductImage
        alt={record.title || 'Барааны зураг'}
        src={record.imageCover}
        size={40}
        preview
        style={{
          objectFit: 'cover',
          borderRadius: 6,
          boxShadow: '2px 2px 4px 0px #00000040',
        }}
      />
    ),
  },
  {
    title: 'Үлдэгдэл',
    dataIndex: 'stock',
    key: 'stock',
    width: 200,
  },
  {
    title: 'Багц ширхэг',
    dataIndex: 'group',
    key: 'group',
    render: (group) => group.length,
    width: 200,
  },
  {
    title: 'Үнэ',
    dataIndex: 'price',
    key: 'price',
    render: (value) => `${(value ?? 0).toLocaleString('mn-MN')} ₮`,
    width: 200,
  },
  {
    title: 'Төлөв',
    dataIndex: 'status',
    key: 'status',
    width: 200,
    filters: [
      { text: 'Идэвхтэй', value: 'ready' },
      { text: 'Идэвхгүй', value: 'unavailable' },
    ],
    ...getColumnFilterProps('status'),
    render: (status, record) =>
      status ? (
        <div className="delivery-status-cell">
          <span className={`product-status-badge product-status-${status}`}>
            <span className="product-status-dot" />
            {STATUS_ENUM[status] ?? status}
          </span>
        </div>
      ) : (
        '-'
      ),
  },
  {
    title: 'Дуусах хугцаатай эсэх',
    dataIndex: 'willExpire',
    key: 'willExpire',
    width: 200,
    render: (willExpire, record) =>
      willExpire == null ? (
        '-'
      ) : (
        <div className="delivery-status-cell">
          <span className={`expire-status-badge expire-status-${willExpire}`}>
            <span className="expire-status-dot" />
            {EXPIRE_ENUM[willExpire] ?? willExpire}
          </span>
        </div>
      ),
  },
];

const List = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = async ({ page = 1, pageSize = 10, ...filters } = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/v1.0/product/listByGroup', {
        page,
        pageSize,
        ...filters,
      });
      setData(data?.result?.docs || []);
      setMeta(data?.result?.meta || {});
      setPagination({
        current: data?.result?.page ?? page,
        pageSize: data?.result?.limit ?? pageSize,
        total: data?.result?.totalDocs ?? 0,
      });
    } catch (e) {
      console.error('Product group list fetch error: ', e);
    } finally {
      setLoading(false);
    }
  };

  const { getColumnSearchProps, getColumnFilterProps, handleTableChange, withHighlight } =
    useTableFilter({
      fetchData,
    });

  const { columns, settingsProps, onColumnOrderChange } = useColumnSettings({
    columns: withHighlight(
      getColumns(location, getColumnSearchProps, getColumnFilterProps, pagination, meta)
    ),
    storageKey: 'productGroup.list',
  });

  return (
    <>
      <PPPageHeader
        title="Багц барааны жагсаалт"
        breadcrumb={{ routes }}
        extra={
          <Space>
            <ColumnSettings {...settingsProps} />
            <Button
              type="primary"
              onClick={() => {
                history.push('/merchant/group/product/add');
              }}
              icon={<PlusOutlined />}
            >
              Бараа нэмэх
            </Button>
          </Space>
        }
      />
      <div className="pp_table_wrapper">
        <PPResizableTable
          dataSource={data}
          columns={columns}
          onColumnOrderChange={onColumnOrderChange}
          size="middle"
          rowKey="_id"
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Нийт ${total} илэрц`,
          }}
          scroll={{ x: 2000 }}
        />
      </div>
    </>
  );
};

export default List;
